import { useEffect, useRef, useState } from 'react';
import { SearchingIcon } from '@icons';

import { inputStyles } from '@components/input/input.config';

import Icon from '../icon/Icon';

type SlideState = 'visible' | 'exit' | 'enter';

const SLIDE_CLASS: Record<SlideState, string> = {
  visible: 'translate-y-0',
  exit: '-translate-y-full',
  enter: 'translate-y-full'
};

const ANIMATED_PLACEHOLDER_INTERVAL = 3700;
const ANIMATED_PLACEHOLDER_TRANSITION = 700;
const ANIMATED_PLACEHOLDER_INITIAL_DELAY = 1500;

interface SearchBarProps {
  inputId?: string;
  placeholder: string;
  animatedPlaceholders?: string[];
  animatedPlaceholderPrefix?: string;
  SearchIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  onSearch: (searchQuery: string) => void;
  searchBarStyle?: string;
  iconStyle?: string;
  searchInputStyle?: string;
  value?: string;
  clearSearchInput?: boolean;
  stopEventPropagation?: boolean;
  isSearchDigitsOnly?: boolean;
  debounceDelay?: number;
}

const SearchBar = ({
  inputId,
  placeholder,
  animatedPlaceholders,
  animatedPlaceholderPrefix = '',
  SearchIcon = SearchingIcon,
  onSearch,
  searchBarStyle,
  iconStyle,
  searchInputStyle,
  value = '',
  clearSearchInput = false,
  stopEventPropagation = false,
  isSearchDigitsOnly = false,
  debounceDelay = 1000
}: SearchBarProps) => {
  const [searchText, setSearchText] = useState<string>(value);
  const [wordIndex, setWordIndex] = useState(0);
  const [slideState, setSlideState] = useState<SlideState>('visible');
  const [hasTransition, setHasTransition] = useState(true);

  // 🔑 debounce reference
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const triggerSearch = (query: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onSearch(query);
    }, debounceDelay);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: searchValue } = event.target;

    if (isSearchDigitsOnly && searchValue && !/^[0-9]+$/.test(searchValue)) {
      return;
    }

    setSearchText(searchValue);
    triggerSearch(searchValue);

    if (stopEventPropagation) {
      event.stopPropagation();
    }
  };

  const handleSearchBarClear = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    setSearchText('');
    onSearch('');
  };

  const onInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (stopEventPropagation) {
      event.stopPropagation();
    }
  };

  // Sync external value → local state
  useEffect(() => {
    setSearchText(value);
  }, [value]);

  // Clear search when triggered by parent
  useEffect(() => {
    if (clearSearchInput) {
      handleSearchBarClear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSearchInput]);

  // 🧹 Cleanup debounce on unmount
  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    []
  );

  useEffect(() => {
    let t1: NodeJS.Timeout | undefined;
    let t2: NodeJS.Timeout | undefined;
    let interval: NodeJS.Timeout | undefined;
    let initialDelay: NodeJS.Timeout | undefined;

    if (animatedPlaceholders?.length) {
      const rotate = () => {
        setSlideState('exit');

        t1 = setTimeout(() => {
          setHasTransition(false);
          setWordIndex((prev) => (prev + 1) % animatedPlaceholders.length);
          setSlideState('enter');

          t2 = setTimeout(() => {
            setHasTransition(true);
            setSlideState('visible');
          }, 10);
        }, ANIMATED_PLACEHOLDER_TRANSITION);
      };

      initialDelay = setTimeout(() => {
        rotate();
        interval = setInterval(rotate, ANIMATED_PLACEHOLDER_INTERVAL);
      }, ANIMATED_PLACEHOLDER_INITIAL_DELAY);
    }

    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [animatedPlaceholders]);

  const wordSlideClass = `flex items-center h-full ${
    hasTransition ? 'transition-transform duration-700' : 'transition-none'
  } ${SLIDE_CLASS[slideState]}`;

  return (
    <div className={`relative h-11 ${searchBarStyle || ''}`}>
      {SearchIcon && (
        <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
          <SearchIcon className={iconStyle || ''} />
        </div>
      )}

      <input
        data-testid={inputId}
        value={searchText}
        placeholder={animatedPlaceholders ? '' : placeholder}
        onChange={handleInputChange}
        onClick={onInputClick}
        type="text"
        className={`size-full border-grey30 bg-white py-2 pl-10 pr-9 text-sm placeholder:text-disabledContent ${
          inputStyles.common
        } ${searchInputStyle || ''}`}
      />

      {animatedPlaceholders && !searchText && (
        <div className="pointer-events-none absolute inset-y-0 left-10 flex items-center gap-1 text-sm text-disabledContent">
          {animatedPlaceholderPrefix ? (
            <span>{animatedPlaceholderPrefix}</span>
          ) : null}
          <div className="flex h-[1.25em] items-center overflow-hidden">
            <span className={wordSlideClass}>
              {animatedPlaceholders[wordIndex]}
            </span>
          </div>
        </div>
      )}

      {searchText && (
        <div
          role="presentation"
          onClick={handleSearchBarClear}
          className="absolute inset-y-0 right-4 flex cursor-pointer items-center"
        >
          <Icon name="close" />
        </div>
      )}
    </div>
  );
};

export default SearchBar;
